import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Accordion, Badge, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import superAdminApiClient from '../../../api/superAdminApiClient';

const emptySection = {
    section_key: '',
    title: '',
    title_highlight: '',
    subtitle: '',
    description: '',
    display_type: 'grid',
    is_slider: false,
    items_per_view: '',
    button_text: '',
    button_link: '',
    sort_order: 0,
    status: 'active',
    settings_json: '',
};

const emptyItem = {
    title: '',
    subtitle: '',
    description: '',
    image_url: '',
    link_url: '',
    button_text: '',
    badge_text: '',
    price_text: '',
    sort_order: 0,
    status: 'active',
    metadata_json: '',
};

const toJsonText = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return JSON.stringify(value, null, 2);
};

// Auto-generate a snake_case section key from a human-readable title
const generateSectionKey = (title) =>
    title
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_{2,}/g, '_')
        .replace(/^_+|_+$/g, '');

const appendFormData = (data, file) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value ?? '');
    });
    if (file) formData.append('image', file);
    return formData;
};

const FrontendPageEditor = () => {
    const { pageKey } = useParams();
    const [page, setPage] = useState(null);
    const [pageForm, setPageForm] = useState({ title: '', slug: '', meta_title: '', meta_desc: '', meta_keywords: '', status: 'active' });
    const [loading, setLoading] = useState(true);
    const [savingPage, setSavingPage] = useState(false);
    const [showSectionModal, setShowSectionModal] = useState(false);
    const [editingSection, setEditingSection] = useState(null);
    const [sectionForm, setSectionForm] = useState(emptySection);
    const [sectionImage, setSectionImage] = useState(null);
    const [showItemModal, setShowItemModal] = useState(false);
    const [activeSection, setActiveSection] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [itemForm, setItemForm] = useState(emptyItem);
    const [itemImage, setItemImage] = useState(null);

    const fetchPage = async () => {
        setLoading(true);
        try {
            const response = await superAdminApiClient.get(`/superadmin/web-pages/frontend-pages/${pageKey}`);
            const data = response.data.data;
            setPage(data);
            setPageForm({
                title: data.title || '',
                slug: data.slug || '',
                meta_title: data.meta_title || '',
                meta_desc: data.meta_desc || '',
                meta_keywords: data.meta_keywords || '',
                status: data.status || 'active',
            });
        } catch (error) {
            toast.error('Failed to fetch header page');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPage();
    }, [pageKey]);

    const handlePageSubmit = async (e) => {
        e.preventDefault();
        setSavingPage(true);
        try {
            await superAdminApiClient.put(`/superadmin/web-pages/frontend-pages/${pageKey}`, pageForm);
            toast.success('Page details updated');
            fetchPage();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update page');
        } finally {
            setSavingPage(false);
        }
    };

    const openSectionModal = (section = null) => {
        setEditingSection(section);
        setSectionImage(null);
        setSectionForm(section ? {
            section_key: section.section_key || '',
            title: section.title || '',
            title_highlight: section.title_highlight || '',
            subtitle: section.subtitle || '',
            description: section.description || '',
            display_type: section.display_type || 'grid',
            is_slider: !!section.is_slider,
            items_per_view: section.items_per_view || '',
            button_text: section.button_text || '',
            button_link: section.button_link || '',
            sort_order: section.sort_order || 0,
            status: section.status || 'active',
            settings_json: toJsonText(section.settings_json),
        } : emptySection);
        setShowSectionModal(true);
    };

    const handleSectionSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = appendFormData({ ...sectionForm, is_slider: sectionForm.is_slider ? 'true' : 'false' }, sectionImage);
            if (editingSection) {
                await superAdminApiClient.put(`/superadmin/web-pages/frontend-sections/${editingSection.id}`, payload);
                toast.success('Section updated');
            } else {
                await superAdminApiClient.post(`/superadmin/web-pages/frontend-pages/${pageKey}/sections`, payload);
                toast.success('Section created');
            }
            setShowSectionModal(false);
            fetchPage();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save section');
        }
    };

    const deleteSection = (section) => {
        Swal.fire({
            title: 'Delete Section?',
            text: 'This will delete all items inside this section.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it'
        }).then(async (result) => {
            if (!result.isConfirmed) return;
            try {
                await superAdminApiClient.delete(`/superadmin/web-pages/frontend-sections/${section.id}`);
                toast.success('Section deleted');
                fetchPage();
            } catch (error) {
                toast.error('Failed to delete section');
            }
        });
    };

    const toggleSection = async (section) => {
        try {
            await superAdminApiClient.patch(`/superadmin/web-pages/frontend-sections/${section.id}/status`);
            fetchPage();
        } catch (error) {
            toast.error('Failed to update section status');
        }
    };

    const openItemModal = (section, item = null) => {
        setActiveSection(section);
        setEditingItem(item);
        setItemImage(null);
        setItemForm(item ? {
            title: item.title || '',
            subtitle: item.subtitle || '',
            description: item.description || '',
            image_url: item.image_url || '',
            link_url: item.link_url || '',
            button_text: item.button_text || '',
            badge_text: item.badge_text || '',
            price_text: item.price_text || '',
            sort_order: item.sort_order || 0,
            status: item.status || 'active',
            metadata_json: toJsonText(item.metadata_json),
        } : { ...emptyItem, sort_order: (section.Items || []).length + 1 });
        setShowItemModal(true);
    };

    const handleItemSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = appendFormData(itemForm, itemImage);
            if (editingItem) {
                await superAdminApiClient.put(`/superadmin/web-pages/frontend-section-items/${editingItem.id}`, payload);
                toast.success('Item updated');
            } else {
                await superAdminApiClient.post(`/superadmin/web-pages/frontend-sections/${activeSection.id}/items`, payload);
                toast.success('Item created');
            }
            setShowItemModal(false);
            fetchPage();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save item');
        }
    };

    const deleteItem = (item) => {
        Swal.fire({
            title: 'Delete Item?',
            text: 'This item will be removed from the page section.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it'
        }).then(async (result) => {
            if (!result.isConfirmed) return;
            try {
                await superAdminApiClient.delete(`/superadmin/web-pages/frontend-section-items/${item.id}`);
                toast.success('Item deleted');
                fetchPage();
            } catch (error) {
                toast.error('Failed to delete item');
            }
        });
    };

    const toggleItem = async (item) => {
        try {
            await superAdminApiClient.patch(`/superadmin/web-pages/frontend-section-items/${item.id}/status`);
            fetchPage();
        } catch (error) {
            toast.error('Failed to update item status');
        }
    };

    if (loading) {
        return (
            <div className="container-fluid py-5 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!page) {
        return <div className="container-fluid"><div className="alert alert-warning">Header page not found.</div></div>;
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="page-title-box d-sm-flex align-items-center justify-content-between m-0 mb-3">
                        <div>
                            <h4 className="mb-sm-0 font-weight-bold text-dark">Edit {page.title}</h4>
                            <code className="text-primary">{page.page_key}</code>
                        </div>
                        <Link to="/superadmin/web-pages/header-pages" className="btn btn-light border">
                            <i className="ri-arrow-left-line me-1"></i> Back
                        </Link>
                    </div>
                </div>
            </div>

            <Row className="g-4">
                <Col xl={4}>
                    <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                        <Card.Body>
                            <h5 className="font-weight-bold text-dark mb-3">Page Details</h5>
                            <Form onSubmit={handlePageSubmit}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Page Title</Form.Label>
                                    <Form.Control value={pageForm.title} onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })} required />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Slug</Form.Label>
                                    <Form.Control value={pageForm.slug} onChange={(e) => setPageForm({ ...pageForm, slug: e.target.value })} required />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Meta Title</Form.Label>
                                    <Form.Control value={pageForm.meta_title} onChange={(e) => setPageForm({ ...pageForm, meta_title: e.target.value })} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Meta Description</Form.Label>
                                    <Form.Control as="textarea" rows={3} value={pageForm.meta_desc} onChange={(e) => setPageForm({ ...pageForm, meta_desc: e.target.value })} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Meta Keywords</Form.Label>
                                    <Form.Control value={pageForm.meta_keywords} onChange={(e) => setPageForm({ ...pageForm, meta_keywords: e.target.value })} />
                                </Form.Group>
                                <Form.Group className="mb-4">
                                    <Form.Check
                                        type="switch"
                                        id="page-status"
                                        label="Active page"
                                        checked={pageForm.status === 'active'}
                                        onChange={(e) => setPageForm({ ...pageForm, status: e.target.checked ? 'active' : 'inactive' })}
                                    />
                                </Form.Group>
                                <Button type="submit" variant="primary" disabled={savingPage}>
                                    <i className="ri-save-line me-1"></i> Save Page
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xl={8}>
                    <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                        <Card.Body>
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <h5 className="font-weight-bold text-dark mb-0">Sections</h5>
                                <Button variant="primary" onClick={() => openSectionModal()}>
                                    <i className="ri-add-line me-1"></i> Add Section
                                </Button>
                            </div>

                            <Accordion alwaysOpen>
                                {(page.Sections || []).map((section) => (
                                    <Accordion.Item eventKey={`${section.id}`} key={section.id} className="border mb-3">
                                        <Accordion.Header>
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                <span className="font-weight-semibold text-dark">{section.title_highlight ? `${section.title_highlight} ` : ''}{section.title || section.section_key}</span>
                                                <Badge bg={section.is_slider ? 'info' : 'light'} className={section.is_slider ? '' : 'text-dark border'}>{section.is_slider ? 'Slider' : section.display_type}</Badge>
                                                <Badge bg={section.status === 'active' ? 'success' : 'secondary'}>{section.status}</Badge>
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <div className="text-muted fs-13">
                                                    Key: <code>{section.section_key}</code> | Items: {(section.Items || []).length}
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <Button variant="light" size="sm" className="border" onClick={() => toggleSection(section)}>
                                                        {section.status === 'active' ? 'Deactivate' : 'Activate'}
                                                    </Button>
                                                    <Button variant="light" size="sm" className="border" onClick={() => openSectionModal(section)}>
                                                        <i className="ri-pencil-line"></i>
                                                    </Button>
                                                    <Button variant="light" size="sm" className="border text-danger" onClick={() => deleteSection(section)}>
                                                        <i className="ri-delete-bin-line"></i>
                                                    </Button>
                                                    <Button variant="primary" size="sm" onClick={() => openItemModal(section)}>
                                                        <i className="ri-add-line"></i> Item
                                                    </Button>
                                                </div>
                                            </div>

                                            <div className="table-responsive">
                                                <Table className="table align-middle mb-0">
                                                    <thead className="table-light text-muted">
                                                        <tr>
                                                            <th>Title</th>
                                                            <th>Link</th>
                                                            <th>Status</th>
                                                            <th style={{ width: '120px' }} className="text-center">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(section.Items || []).length === 0 ? (
                                                            <tr><td colSpan="4" className="text-center text-muted py-4">No items in this section.</td></tr>
                                                        ) : section.Items.map((item) => (
                                                            <tr key={item.id}>
                                                                <td>
                                                                    <div className="font-weight-semibold text-dark">{item.title || '-'}</div>
                                                                    <div className="text-muted fs-12">{item.subtitle || item.description || ''}</div>
                                                                </td>
                                                                <td className="text-muted fs-13">{item.link_url || '-'}</td>
                                                                <td>
                                                                    <Badge bg={item.status === 'active' ? 'success' : 'secondary'}>{item.status}</Badge>
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex gap-2 justify-content-center">
                                                                        <Button variant="light" size="sm" className="border" onClick={() => toggleItem(item)}>
                                                                            <i className="ri-refresh-line"></i>
                                                                        </Button>
                                                                        <Button variant="light" size="sm" className="border" onClick={() => openItemModal(section, item)}>
                                                                            <i className="ri-pencil-line"></i>
                                                                        </Button>
                                                                        <Button variant="light" size="sm" className="border text-danger" onClick={() => deleteItem(item)}>
                                                                            <i className="ri-delete-bin-line"></i>
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={showSectionModal} onHide={() => setShowSectionModal(false)} size="lg" centered className="text-dark">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title>{editingSection ? 'Edit Section' : 'Add Section'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSectionSubmit}>
                    <Modal.Body>
                        <Row className="g-3">

                            {/* ── Display Type ── */}
                            <Col md={12}>
                                <Form.Label className="fw-semibold">Display Type <span className="text-danger">*</span></Form.Label>
                                <Form.Select value={sectionForm.display_type} onChange={(e) => setSectionForm({ ...sectionForm, display_type: e.target.value })}>
                                    <option value="grid">Grid — image cards in a grid layout</option>
                                    <option value="destination_grid">Destination Grid — compact destination cards</option>
                                    <option value="image_slider">Image Slider — horizontal scrolling slider</option>
                                    <option value="destination_slider">Destination Slider — Swiper slider for destinations</option>
                                    <option value="offer_cards">Offer Cards — large promotional cards with price &amp; badge</option>
                                    <option value="package_cards">Package Cards — holiday/tour package cards</option>
                                    <option value="benefits">Benefits — icon + text benefit tiles</option>
                                    <option value="banner">Banner — full-width single banner with CTA</option>
                                    <option value="banners">Banners — multiple banner cards in a row</option>
                                    <option value="hero">Hero — full-width hero section with background</option>
                                    <option value="listing_heading">Listing Heading — text heading with optional CTA button</option>
                                    <option value="cab_routes">Cab Routes — cab route cards in a grid</option>
                                    <option value="cab_slider">Cab Slider — cab cards in a slider</option>
                                </Form.Select>
                                <Form.Text className="text-muted">Determines how this section is visually rendered on the frontend.</Form.Text>
                            </Col>

                            {/* ── Title Highlight + Title ── */}
                            <Col md={4}>
                                <Form.Label className="fw-semibold">Title Highlight</Form.Label>
                                <Form.Control
                                    value={sectionForm.title_highlight}
                                    onChange={(e) => setSectionForm({ ...sectionForm, title_highlight: e.target.value })}
                                    placeholder="e.g. Explore"
                                />
                                <Form.Text className="text-muted">Coloured/bold word shown before the main title.</Form.Text>
                            </Col>
                            <Col md={8}>
                                <Form.Label className="fw-semibold">Title</Form.Label>
                                <Form.Control
                                    value={sectionForm.title}
                                    placeholder="e.g. Holiday Destinations"
                                    onChange={(e) => {
                                        const newTitle = e.target.value;
                                        setSectionForm(prev => ({
                                            ...prev,
                                            title: newTitle,
                                            // Auto-generate key only for new sections
                                            ...(!editingSection && { section_key: generateSectionKey(newTitle) }),
                                        }));
                                    }}
                                />
                                <Form.Text className="text-muted">Main heading shown on the frontend section.</Form.Text>
                            </Col>

                            {/* ── Section Key ── */}
                            <Col md={12}>
                                <Form.Label className="fw-semibold">
                                    Section Key <span className="text-danger">*</span>
                                    {!editingSection && (
                                        <span className="badge bg-info text-dark ms-2" style={{ fontSize: '0.7rem', fontWeight: 500 }}>
                                            Auto-generated from Title
                                        </span>
                                    )}
                                </Form.Label>
                                <Form.Control
                                    value={sectionForm.section_key}
                                    onChange={(e) => setSectionForm({ ...sectionForm, section_key: e.target.value })}
                                    required
                                    disabled={!!editingSection}
                                    placeholder="e.g. holiday_destinations"
                                    style={!editingSection ? { fontFamily: 'monospace', background: '#f8f9fa' } : { fontFamily: 'monospace' }}
                                />
                                <Form.Text className="text-muted">
                                    {editingSection
                                        ? 'Section key cannot be changed after creation.'
                                        : 'Unique identifier used internally. Auto-filled from Title — you can edit it. Use lowercase letters and underscores only (e.g. summer_specials).'}
                                </Form.Text>
                            </Col>

                            {/* ── Subtitle + Description ── */}
                            <Col md={12}>
                                <Form.Label className="fw-semibold">Subtitle</Form.Label>
                                <Form.Control as="textarea" rows={2} value={sectionForm.subtitle} placeholder="Short tagline shown below the section title" onChange={(e) => setSectionForm({ ...sectionForm, subtitle: e.target.value })} />
                            </Col>
                            <Col md={12}>
                                <Form.Label className="fw-semibold">Description</Form.Label>
                                <Form.Control as="textarea" rows={2} value={sectionForm.description} placeholder="Optional longer description for the section" onChange={(e) => setSectionForm({ ...sectionForm, description: e.target.value })} />
                            </Col>

                            {/* ── Button CTA ── */}
                            <Col md={6}>
                                <Form.Label className="fw-semibold">Button Text</Form.Label>
                                <Form.Control
                                    value={sectionForm.button_text}
                                    placeholder="e.g. View All Holidays"
                                    onChange={(e) => setSectionForm({ ...sectionForm, button_text: e.target.value })}
                                />
                                <Form.Text className="text-muted">
                                    A call-to-action button displayed below this section's content. Leave blank to hide the button.
                                </Form.Text>
                            </Col>
                            <Col md={6}>
                                <Form.Label className="fw-semibold">Button Link</Form.Label>
                                <Form.Control
                                    value={sectionForm.button_link}
                                    placeholder="e.g. /holidays-list"
                                    onChange={(e) => setSectionForm({ ...sectionForm, button_link: e.target.value })}
                                />
                                <Form.Text className="text-muted">
                                    URL the button navigates to. Use relative paths (e.g. /holidays-list) or full URLs.
                                </Form.Text>
                            </Col>

                            {/* ── Sort Order + Items Per View + Background Image ── */}
                            <Col md={4}>
                                <Form.Label className="fw-semibold">Sort Order</Form.Label>
                                <Form.Control type="number" min="0" value={sectionForm.sort_order} onChange={(e) => setSectionForm({ ...sectionForm, sort_order: e.target.value })} />
                                <Form.Text className="text-muted">Lower number = appears higher on the page (0 = top).</Form.Text>
                            </Col>
                            <Col md={4}>
                                <Form.Label className="fw-semibold">Items Per View</Form.Label>
                                <Form.Control type="number" min="1" max="12" value={sectionForm.items_per_view} onChange={(e) => setSectionForm({ ...sectionForm, items_per_view: e.target.value })} />
                                <Form.Text className="text-muted">How many items to show per row/slide on desktop (e.g. 4).</Form.Text>
                            </Col>
                            <Col md={4}>
                                <Form.Label className="fw-semibold">Background Image</Form.Label>
                                <Form.Control type="file" accept="image/*" onChange={(e) => setSectionImage(e.target.files?.[0] || null)} />
                                <Form.Text className="text-muted">Used for Banner/Hero sections as the background.</Form.Text>
                            </Col>

                            {/* ── Toggles ── */}
                            <Col md={6}>
                                <Form.Check
                                    type="switch" id="section-slider"
                                    label="Display items as slider"
                                    checked={sectionForm.is_slider}
                                    onChange={(e) => setSectionForm({ ...sectionForm, is_slider: e.target.checked })}
                                />
                                <Form.Text className="text-muted">Enable auto-scrolling carousel for this section's items.</Form.Text>
                            </Col>
                            <Col md={6}>
                                <Form.Check
                                    type="switch" id="section-status"
                                    label="Active section"
                                    checked={sectionForm.status === 'active'}
                                    onChange={(e) => setSectionForm({ ...sectionForm, status: e.target.checked ? 'active' : 'inactive' })}
                                />
                                <Form.Text className="text-muted">Inactive sections are hidden from the frontend.</Form.Text>
                            </Col>

                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="border-0">
                        <Button variant="light" className="border" onClick={() => setShowSectionModal(false)}>Cancel</Button>
                        <Button type="submit" variant="primary">Save Section</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={showItemModal} onHide={() => setShowItemModal(false)} size="lg" centered className="text-dark">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title>{editingItem ? 'Edit Item' : 'Add Item'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleItemSubmit}>
                    <Modal.Body>
                        <Row className="g-3">

                            {/* ── Title + Subtitle ── */}
                            <Col md={6}>
                                <Form.Label className="fw-semibold">Title</Form.Label>
                                <Form.Control
                                    value={itemForm.title}
                                    placeholder="e.g. Bali"
                                    onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                                />
                                <Form.Text className="text-muted">Main label shown on the card or slide.</Form.Text>
                            </Col>
                            <Col md={6}>
                                <Form.Label className="fw-semibold">Subtitle</Form.Label>
                                <Form.Control
                                    value={itemForm.subtitle}
                                    placeholder="e.g. 5 Nights / 6 Days"
                                    onChange={(e) => setItemForm({ ...itemForm, subtitle: e.target.value })}
                                />
                                <Form.Text className="text-muted">Secondary text shown below the title.</Form.Text>
                            </Col>

                            {/* ── Description ── */}
                            <Col md={12}>
                                <Form.Label className="fw-semibold">Description</Form.Label>
                                <Form.Control
                                    as="textarea" rows={2}
                                    value={itemForm.description}
                                    placeholder="Optional longer description for this item"
                                    onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                                />
                            </Col>

                            {/* ── Image ── */}
                            <Col md={6}>
                                <Form.Label className="fw-semibold">Image URL</Form.Label>
                                <Form.Control
                                    value={itemForm.image_url}
                                    placeholder="https://... or assets/img/..."
                                    onChange={(e) => setItemForm({ ...itemForm, image_url: e.target.value })}
                                />
                                <Form.Text className="text-muted">Paste an existing image URL, or upload a new image below.</Form.Text>
                            </Col>
                            <Col md={6}>
                                <Form.Label className="fw-semibold">Upload New Image</Form.Label>
                                <Form.Control type="file" accept="image/*" onChange={(e) => setItemImage(e.target.files?.[0] || null)} />
                                <Form.Text className="text-muted">Uploading a new image will replace the URL above.</Form.Text>
                            </Col>

                            {/* ── Link + Button ── */}
                            <Col md={6}>
                                <Form.Label className="fw-semibold">Link URL</Form.Label>
                                <Form.Control
                                    value={itemForm.link_url}
                                    placeholder="e.g. /holiday-details/42"
                                    onChange={(e) => setItemForm({ ...itemForm, link_url: e.target.value })}
                                />
                                <Form.Text className="text-muted">Where the user goes when they click this item/card.</Form.Text>
                            </Col>
                            <Col md={6}>
                                <Form.Label className="fw-semibold">Button Text</Form.Label>
                                <Form.Control
                                    value={itemForm.button_text}
                                    placeholder="e.g. Book Now"
                                    onChange={(e) => setItemForm({ ...itemForm, button_text: e.target.value })}
                                />
                                <Form.Text className="text-muted">
                                    Label for the CTA button on this card (uses Link URL above). Leave blank to hide the button.
                                </Form.Text>
                            </Col>

                            {/* ── Badge + Price + Sort Order ── */}
                            <Col md={4}>
                                <Form.Label className="fw-semibold">Badge</Form.Label>
                                <Form.Control
                                    value={itemForm.badge_text}
                                    placeholder="e.g. Hot Deal"
                                    onChange={(e) => setItemForm({ ...itemForm, badge_text: e.target.value })}
                                />
                                <Form.Text className="text-muted">
                                    Small label shown as a tag/chip on the card (e.g. "New", "Hot Deal", "Limited").
                                </Form.Text>
                            </Col>
                            <Col md={4}>
                                <Form.Label className="fw-semibold">Price Text</Form.Label>
                                <Form.Control
                                    value={itemForm.price_text}
                                    placeholder="e.g. ₹12,999"
                                    onChange={(e) => setItemForm({ ...itemForm, price_text: e.target.value })}
                                />
                                <Form.Text className="text-muted">
                                    Display price shown on the card (e.g. "₹12,999", "Starting ₹8,500/person").
                                </Form.Text>
                            </Col>
                            <Col md={4}>
                                <Form.Label className="fw-semibold">Sort Order</Form.Label>
                                <Form.Control
                                    type="number" min="0"
                                    value={itemForm.sort_order}
                                    onChange={(e) => setItemForm({ ...itemForm, sort_order: e.target.value })}
                                />
                                <Form.Text className="text-muted">
                                    Controls position within this section. Lower number = shown first (0 = first).
                                </Form.Text>
                            </Col>

                            {/* ── Status ── */}
                            <Col md={12}>
                                <Form.Check
                                    type="switch" id="item-status"
                                    label="Active item"
                                    checked={itemForm.status === 'active'}
                                    onChange={(e) => setItemForm({ ...itemForm, status: e.target.checked ? 'active' : 'inactive' })}
                                />
                                <Form.Text className="text-muted">Inactive items are hidden from the frontend.</Form.Text>
                            </Col>

                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="border-0">
                        <Button variant="light" className="border" onClick={() => setShowItemModal(false)}>Cancel</Button>
                        <Button type="submit" variant="primary">Save Item</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default FrontendPageEditor;
